import React from 'react';

import './ConfidenceLabel.css';

export type ConfidenceLevel = 'lowest' | 'low' | 'high' | 'highest';

export type ConfidenceLabelProps = {
  confidenceAutomated?: boolean;
  confidenceValue?: number;
  confidenceLevel?: ConfidenceLevel;
  ignored?: boolean;
};

export type InputProps = ConfidenceLabelProps & React.InputHTMLAttributes<HTMLInputElement>;

/**
 * Helper function to return class names based on ConfidenceLevel.
 * @param confidenceLevel ConfidenceLevel
 * @returns string class names
 */
const getConfidenceClassName = (confidenceLevel?: ConfidenceLevel) =>
  `${confidenceLevel ? `confidence-label-${confidenceLevel}` : ''}`;

/**
 * Renders a confidence "label".
 * If none of the props are passed down, returns null.
 * If the confidenceAutomated prop is true, displays a check mark icon.
 * Otherwise, displays a confidence percentage value.
 *
 * @param param0 Confidence
 */
const ConfidenceLabel = ({
  confidenceAutomated,
  confidenceValue,
  confidenceLevel,
  ignored,
}: ConfidenceLabelProps): JSX.Element | null => {
  if (ignored) {
    return <span className="confidence-label confidence-label-ignored fe fe-minus-circle ml-2" role="img" />;
  }

  if (!confidenceAutomated && !confidenceValue && !confidenceLevel) {
    return null;
  }

  if (confidenceAutomated) {
    return <span className="confidence-label confidence-label-automated fe fe-check-circle ml-2" role="img" />;
  }

  const confidenceClass = getConfidenceClassName(confidenceLevel);
  return (
    <span
      className={`badge confidence-label font-weight-bold ml-2${confidenceClass ? ` ${confidenceClass}` : ''}`}
      data-testid="confidence-label-level"
    >
      {confidenceValue && confidenceLevel && `${confidenceValue}%`}
    </span>
  );
};

export default ConfidenceLabel;
